import { DateRuleGroup } from '@/pages/PersonAvailability/components/DateSpecificHoursSection/types';
import { AvailabilityRule, TimeInterval } from '@/types/personAvailability';
import { convertTo24Hour } from '@/utils/timeFormat';

/**
 * Generates an array of dates in YYYY-MM-DD format between startDate and endDate (inclusive).
 * If endDate is not provided or same as startDate, returns a single-element array.
 * Uses UTC to avoid DST transition issues when iterating through dates.
 */
export function generateDateRange(startDate: string, endDate: string): string[] {
  if (!endDate || startDate === endDate) {
    return [startDate];
  }

  const [startYear, startMonth, startDay] = startDate.split('-').map(Number);
  const [endYear, endMonth, endDay] = endDate.split('-').map(Number);

  const start = new Date(Date.UTC(startYear, startMonth - 1, startDay));
  const end = new Date(Date.UTC(endYear, endMonth - 1, endDay));

  if (end < start) {
    return [startDate];
  }

  const dates: string[] = [];
  const current = new Date(start);
  while (current <= end) {
    const year = current.getUTCFullYear();
    const month = String(current.getUTCMonth() + 1).padStart(2, '0');
    const day = String(current.getUTCDate()).padStart(2, '0');
    dates.push(`${year}-${month}-${day}`);
    current.setUTCDate(current.getUTCDate() + 1);
  }

  return dates;
}

/**
 * Returns the current date in YYYY-MM-DD format (local timezone).
 */
export function getCurrentDate(): string {
  return new Date().toLocaleDateString('sv');
}

/**
 * Formats a single date string (YYYY-MM-DD) for display.
 * Example: "2025-12-03" -> "Dec 3, 2025"
 */
export function formatDate(dateStr: string): string {
  const [year, month, day] = dateStr.split('-').map(Number);
  const date = new Date(year, month - 1, day);
  return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
}

/**
 * Formats a date range group for display.
 * Examples:
 * - Single date: "Dec 3, 2025"
 * - Same month: "Dec 3 - 7, 2025"
 * - Different months: "Dec 28 - Jan 3, 2025"
 * - Different years: "Dec 28, 2024 - Jan 3, 2025"
 */
export function formatDateRange(group: DateRuleGroup): string {
  if (group.startDate === group.endDate) {
    return formatDate(group.startDate);
  }

  const [startYear, startMonth, startDay] = group.startDate.split('-').map(Number);
  const [endYear, endMonth, endDay] = group.endDate.split('-').map(Number);

  const startDate = new Date(startYear, startMonth - 1, startDay);
  const endDate = new Date(endYear, endMonth - 1, endDay);

  if (startYear === endYear) {
    if (startMonth === endMonth) {
      return `${startDate.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })} - ${endDay}, ${endYear}`;
    }
    return `${startDate.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })} - ${endDate.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}, ${endYear}`;
  }

  return `${formatDate(group.startDate)} - ${formatDate(group.endDate)}`;
}

/**
 * Groups consecutive date rules with identical intervals into ranges.
 * This allows displaying "Dec 3 - 7, 2025" instead of 5 separate dates.
 *
 * Rules are considered part of the same group if:
 * 1. They are consecutive days (no gaps)
 * 2. They have identical time intervals (normalized to 24-hour format)
 */
export function groupConsecutiveDates(rules: AvailabilityRule[]): DateRuleGroup[] {
  const dateRules = rules
    .filter((rule) => rule.date && rule.type === 'date')
    .sort((a, b) => (a.date! < b.date! ? -1 : 1));

  if (dateRules.length === 0) return [];

  const groups: DateRuleGroup[] = [];
  let currentGroup: DateRuleGroup | null = null;

  const intervalsMatch = (intervals1: TimeInterval[], intervals2: TimeInterval[]): boolean => {
    if (intervals1.length !== intervals2.length) return false;

    const normalize = (intervals: TimeInterval[]) =>
      intervals.map((int) => ({
        from: convertTo24Hour(int.from),
        to: convertTo24Hour(int.to)
      }));

    const sorted1 = normalize(intervals1).sort((a, b) => a.from.localeCompare(b.from));
    const sorted2 = normalize(intervals2).sort((a, b) => a.from.localeCompare(b.from));

    return sorted1.every((int1, idx) => {
      const int2 = sorted2[idx];
      return int1.from === int2.from && int1.to === int2.to;
    });
  };

  const isConsecutiveDay = (date1: string, date2: string): boolean => {
    const [y1, m1, d1] = date1.split('-').map(Number);
    const [y2, m2, d2] = date2.split('-').map(Number);

    // Use UTC to avoid DST transition issues (where a day might be 23 or 25 hours)
    const date1Obj = new Date(Date.UTC(y1, m1 - 1, d1));
    const date2Obj = new Date(Date.UTC(y2, m2 - 1, d2));

    // Add one day using UTC methods
    const nextDay = new Date(date1Obj);
    nextDay.setUTCDate(nextDay.getUTCDate() + 1);

    return (
      nextDay.getUTCFullYear() === date2Obj.getUTCFullYear() &&
      nextDay.getUTCMonth() === date2Obj.getUTCMonth() &&
      nextDay.getUTCDate() === date2Obj.getUTCDate()
    );
  };

  for (const rule of dateRules) {
    const ruleDate = rule.date!;
    const ruleIntervals = rule.intervals || [];

    if (!currentGroup) {
      currentGroup = {
        startDate: ruleDate,
        endDate: ruleDate,
        dates: [ruleDate],
        intervals: ruleIntervals
      };
    } else if (
      isConsecutiveDay(currentGroup.endDate, ruleDate) &&
      intervalsMatch(currentGroup.intervals, ruleIntervals)
    ) {
      currentGroup.endDate = ruleDate;
      currentGroup.dates.push(ruleDate);
    } else {
      groups.push(currentGroup);
      currentGroup = {
        startDate: ruleDate,
        endDate: ruleDate,
        dates: [ruleDate],
        intervals: ruleIntervals
      };
    }
  }

  if (currentGroup) {
    groups.push(currentGroup);
  }

  return groups;
}
